import React, {
  useState,
  useMemo,
  Fragment,
  memo,
  useEffect,
  useCallback,
  useContext,
} from "react";
import Countdown from "react-countdown";
import _ from "lodash";
import {
  useTable,
  usePagination,
  useFilters,
  useGlobalFilter,
  useSortBy,
  useRowSelect,
} from "react-table";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { format } from "date-fns";
import Swal from "sweetalert2";
import { faAngleUp, faAngleDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog } from "@headlessui/react";
import { Switch } from "@blueprintjs/core";

import { API, Auth, Storage } from "aws-amplify";
import { EmitterStatus } from "../../event";

const Row = ({ row, i, value, getData, where }) => {
  const { user } = useSelector((state) => state.initializeApp);
  const [showModal, setShowModal] = useState(false);

  return (
    <Fragment>
      <tr
        {...row.getRowProps()}
        onClick={() => setShowModal(true)}
        className={classNames(
          "cursor-pointer hover:bg-blueGray-200 transition-all duration-300",
          {
            "bg-white": i % 2 === 0,
            "bg-gray-100": i % 2 !== 0,
          }
        )}
      >
        {row.cells.map((cell, index) => {
          return (
            <td
              className={"p-4 text-gray-700 text-sm text-center"}
              {...cell.getCellProps()}
            >
              {cell.render("Cell")}
            </td>
          );
        })}
      </tr>
      <HolidayDialog
        date={value.date}
        detail={value.detail}
        value={value.value}
        showModal={showModal}
        setShowModal={setShowModal}
        getData={getData}
        where={where}
        isCreate={false}
      />
    </Fragment>
  );
};

const HolidayDialog = ({
  showModal,
  setShowModal,
  getData,
  where,
  isCreate = true,
  date = "",
  detail = "",
  value = true,
}) => {
  const [error, setError] = useState(false);
  const [form, setForm] = useState({
    date: date,
    detail: detail,
    value: value,
  });

  const onSubmit = async () => {
    try {
      const result = await Swal.fire({
        title: "คุณยืนยันที่จะอนุมัติรายการนี้หรือไม่ ?",
        showCancelButton: true,
        confirmButtonText: `บันทึก`,
        cancelButtonText: "ยกเลิก",
        showLoaderOnConfirm: true,
        showLoaderOnDeny: false,
        preConfirm: async () => {
          if (!form.detail || !form.date) {
            setError(true);
            return {
              error: true,
            };
          }
          if (isCreate) {
            await API.post("MoneyHubAdmin", "/holiday", {
              body: {
                date: form.date,
                detail: form.detail,
                value: form.value,
              },
              headers: {
                Authorization: `Bearer ${(await Auth.currentSession())
                  .getIdToken()
                  .getJwtToken()}`,
              },
            });
          } else {
            await API.put("MoneyHubAdmin", "/holiday", {
              body: {
                date: form.date,
                detail: form.detail,
                value: form.value,
              },
              headers: {
                Authorization: `Bearer ${(await Auth.currentSession())
                  .getIdToken()
                  .getJwtToken()}`,
              },
            });
          }
        },
      });
      if (result.value?.error) {
        return await Swal.fire({
          title: "WARNING",
          text: `โปรดกรอกข้อมูลให้ครบถ้วน`,
          icon: "warning",
          confirmButtonText: "ยืนยัน",
        });
      }
      if (!result.isConfirmed) return;
      await Swal.fire({
        title: "SUCCESS",
        text: "อัพเดตข้อมูลสำเร็จ",
        icon: "success",
        confirmButtonText: "ยืนยัน",
      });
      await getData(where);
    } catch (error) {
      console.log("Error", error);
      await Swal.fire({
        title: "ERROR",
        text: "Something went wrong",
        icon: "error",
        confirmButtonText: "ยืนยัน",
      });
    }
  };
  return (
    <Dialog
      open={showModal}
      className="fixed z-10 inset-0 overflow-y-auto"
      onClose={() => setShowModal(false)}
    >
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-white border border-gray-300 rounded max-w-sm mx-auto p-4 z-20">
          <Dialog.Title className="font-black text-lg">
            เพิ่มวันหยุด
          </Dialog.Title>
          <Dialog.Description className="my-4">
            <div>
              <div className="w-32">วันที่</div>
              <input
                className="border border-gray-300 focus:outline-none px-3 py-2 rounded"
                type="text"
                name="date"
                disabled={!isCreate}
                placeholder="เช่น 01/05"
                value={form.date}
                onChange={(e) =>
                  setForm((prev) => {
                    return {
                      ...prev,
                      [e.target.name]: e.target.value,
                    };
                  })
                }
              />
              <span className="text-red-400 mx-2">
                {error && !form.date && "โปรดใส่วันที่หยุด"}
              </span>
            </div>
            <div>
              <div className="w-32">รายละเอียด</div>
              <input
                className="border border-gray-300 focus:outline-none px-3 py-2 rounded"
                type="text"
                name="detail"
                placeholder="เช่น วันแรงงาน"
                value={form.detail}
                onChange={(e) =>
                  setForm((prev) => {
                    return {
                      ...prev,
                      [e.target.name]: e.target.value,
                    };
                  })
                }
              />
              <span className="text-red-400 mx-2">
                {error && !form.detail && "โปรดใส่รายละเอียด"}
              </span>
            </div>
            <Switch
              checked={form.value}
              label="เป็นวันหยุด"
              onChange={(e) =>
                setForm((prev) => {
                  return {
                    ...prev,
                    value: !form.value,
                  };
                })
              }
            />
          </Dialog.Description>
          <button
            className="p-2 bg-blue-500 text-white w-20 mr-4 rounded"
            onClick={onSubmit}
          >
            ตกลง
          </button>
          <button
            className="p-2 text-blue-500 border border-blue-500 w-20 rounded"
            onClick={() => setShowModal(false)}
          >
            ยกเลิก
          </button>
        </div>
      </div>
    </Dialog>
  );
};

function HolidayTable({ data, getData, where }) {
  const { user } = useSelector((state) => state.initializeApp);
  const [showModal, setShowModal] = useState(false);

  const onDelete = (row) => async (e) => {
    e.stopPropagation();

    const result = await Swal.fire({
      title: "คุณยืนยันที่จะลบรายการนี้หรือไม่ ?",
      showCancelButton: true,
      confirmButtonText: `บันทึก`,
      cancelButtonText: "ยกเลิก",
      showLoaderOnConfirm: true,
      showLoaderOnDeny: false,
      preConfirm: async () => {
        try {
          await API.del("MoneyHubAdmin", `/holiday`, {
            queryStringParameters: {
              date: row.original.date,
            },
            headers: {
              Authorization: `Bearer ${(await Auth.currentSession())
                .getIdToken()
                .getJwtToken()}`,
            },
          });
        } catch (error) {
          await EmitterStatus("ERROR", { logMessage: error });
        }
      },
    });
    if (!result.isConfirmed) return;
    await EmitterStatus("SUCCESS");
    await getData(where);
  };

  const columns = useMemo(
    () => [
      {
        Header: "รายละเอียด",
        accessor: "detail",
      },
      {
        Header: "วันที่",
        accessor: "date",
      },
      {
        Header: "เป็นวันหยุด",
        accessor: "value",
        Cell: ({ row }) => {
          return <div>{row.original?.value ? "YES" : "NO"}</div>;
        },
      },
      {
        Header: "",
        accessor: "delete",
        Cell: ({ row }) => {
          return (
            <button
              className="mx-2 px-4 py-1 rounded text-white shadow-sm duration-300 transition-all focus:outline-none bg-red-500 hover:bg-red-600"
              onClick={onDelete(row)}
            >
              ลบ
            </button>
          );
        },
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      columns,
      data,
    });

  return (
    <div className="mx-4 mb-2">
      <div className="bg-white rounded border border-gray-300 w-full overflow-x-auto ">
        <table {...getTableProps()} className="w-full whitespace-no-wrap">
          <thead className=" text-sm font-semibold tracking-wide text-gray-500  border-b dark:border-gray-700 bg-gray-50 dark:text-gray-400 dark:bg-gray-800">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, i) => (
                  <th
                    {...column.getHeaderProps()}
                    className={classNames("px-4 py-3 text-center", {
                      "rounded-tl": i === 0,
                      "rounded-tr": i === headerGroup.headers.length - 1,
                    })}
                  >
                    {column.render("Header")}
                    <span>
                      {" "}
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <FontAwesomeIcon icon={faAngleDown} />
                        ) : (
                          <FontAwesomeIcon icon={faAngleUp} />
                        )
                      ) : (
                        ""
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody
            className="bg-white divide-y dark:divide-gray-700 dark:bg-gray-800 text-gray-700 dark:text-gray-400"
            {...getTableBodyProps()}
          >
            {rows.map((row, i) => {
              prepareRow(row);
              return (
                <Row
                  where={where}
                  key={row.getRowProps().key}
                  i={i}
                  row={row}
                  value={data[i]}
                  getData={getData}
                />
              );
            })}
          </tbody>
        </table>
      </div>
      <button
        className="bg-blue-500 text-white round-xl p-2 my-4 w-full rounded"
        onClick={() => setShowModal(true)}
      >
        เพิ่มวันหยุด
      </button>
      <HolidayDialog
        showModal={showModal}
        setShowModal={setShowModal}
        getData={getData}
        where={where}
      />
    </div>
  );
}

export default HolidayTable;
